import React, { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Spinner } from 'react-bootstrap'
import { PageTitle } from '../../../_metronic/layout/core'
import TicketActionsBar from './components/TicketActionsBar'
import { getErrorCode } from '../../common/api/errors'
import TicketFacts from './components/TicketFacts'
import TimelinePointItem from './components/TimelinePointItem'
import { getTicketCard } from './core/api'
import { BORDER, INK, MONO, MUTED, NO_BREADCRUMBS } from './core/consts'
import { TicketCard } from './core/response.types'
import { matchingTicketCardUrl } from '../../common/MatchingTicketLink'
import { depositStatusLabel } from '../matching/routes/DepositPayoutTimeline/core/consts'

/**
 * Shared by both ticket directions on purpose: the endpoint answers for a deposit and a payout
 * alike, and the two types are to become one entity. The route, however, stays inside its own
 * direction — the split of directions is not this card's to change.
 */
const TicketCardPage: React.FC = () => {
  const { token } = useParams<{ token: string }>()
  const [card, setCard] = useState<TicketCard | null>(null)
  const [error, setError] = useState<string | null>(null)
  /** Действие меняет тикет на сервере — карточку перечитываем, а не правим локально. */
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    if (!token) return

    let cancelled = false
    setCard(null)
    setError(null)

    const load = async () => {
      try {
        const { data } = await getTicketCard(token)

        if (cancelled) return

        if (!data.success) {
          setError(data.errors.general_error ?? 'Не удалось загрузить тикет')

          return
        }

        setCard(data.data)
      } catch (e) {
        if (cancelled) return

        // a mistyped token in the address is "no such ticket" for the reader, not a load failure
        const code = getErrorCode(e)
        setError(
          code === 'TICKET_NOT_FOUND' || code === 'TICKET_TOKEN_INVALID'
            ? 'Тикет не найден'
            : 'Не удалось загрузить тикет',
        )
      }
    }

    void load()

    return () => {
      cancelled = true
    }
  }, [token, reloadKey])

  return (
    <>
      <PageTitle breadcrumbs={NO_BREADCRUMBS}>Тикет</PageTitle>

      {error && (
        <div className='card' style={{ padding: '24px 28px' }}>
          <div className='text-danger' style={{ fontSize: 14 }}>
            {error}
          </div>
        </div>
      )}

      {!error && !card && (
        <div className='d-flex justify-content-center' style={{ padding: 48 }}>
          <Spinner animation='border' variant='primary' />
        </div>
      )}

      {!error && card && (
        <div
          className='d-flex align-items-center'
          style={{ gap: 12, marginBottom: 18, flexWrap: 'wrap' }}
        >
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 600, color: INK }}>
            {card.header.ticketType === 'payment' ? 'Тикет пополнения' : 'Тикет выплаты'}
          </h1>
          <span className='badge badge-light-primary'>{depositStatusLabel(card.header.status)}</span>
          <span style={{ fontFamily: MONO, fontSize: 12.5, color: MUTED }}>{card.header.token}</span>
          <div style={{ flexGrow: 1 }} />
          <TicketActionsBar header={card.header} onChanged={() => setReloadKey((n) => n + 1)} />
          {/* Раньше путь на разбор мэтчинга был только у события отказа в ленте, поэтому у
              принятого мэтчингом депозита его не было вовсе. Условие — то же, по которому
              матчинговая карточка вообще существует, так что кнопка не ведёт в «решения нет». */}
          {card.header.hasMatchingDecision && (
            <Link
              to={matchingTicketCardUrl(card.header.token)}
              className='btn btn-sm btn-light-primary'
            >
              Карточка матчинг-тикета
            </Link>
          )}
        </div>
      )}

      {!error && card && (
        <div className='d-flex' style={{ gap: 18, alignItems: 'flex-start' }}>
          <div style={{ width: 396, flexShrink: 0 }}>
            <TicketFacts header={card.header} />
          </div>

          <div className='card' style={{ flexGrow: 1, minWidth: 0 }}>
            <div
              className='d-flex align-items-center'
              style={{ minHeight: 56, padding: '0 28px', gap: 10, borderBottom: `1px solid ${BORDER}` }}
            >
              <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: INK }}>История тикета</h2>
              <span style={{ fontSize: 12.5, color: MUTED }}>{card.timeline.length} событий</span>
            </div>

            {/* Сказано вслух, а не молча отрисована неполная лента. */}
            {!card.journalAvailable && (
              <div className='alert alert-warning' style={{ margin: '18px 28px 0', fontSize: 13 }}>
                Журнал интеграций не ответил — запрос мерчанта и колбэки в этой ленте не показаны.
              </div>
            )}

            <div className='d-flex flex-column' style={{ padding: '22px 28px' }}>
              {card.timeline.map((point, index) => (
                <TimelinePointItem
                  key={`${point.at}-${point.kind}-${index}`}
                  point={point}
                  last={index === card.timeline.length - 1}
                  ticketToken={card.header.token}
                />
              ))}
              {card.timeline.length === 0 && (
                <div style={{ fontSize: 13, color: MUTED }}>По тикету пока нет событий</div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default TicketCardPage
